import React, { useState } from "react";
import { CalendarDays, Check, ChevronsUpDown, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import WorkingHoursDialog from "@/components/team/WorkingHoursDialog";
import {
  useGetUserPresenceQuery,
  useUpdateUserPresenceMutation,
  useUpdateWorkingHoursMutation,
} from "@/store/rest_api";
import { AvailabilityMode, UserPresenceStatusRequest } from "@/model/interface/Membership";
import {
  browserTimeZone,
  describeAvailability,
  hasWorkingHours,
  isSetUnavailable,
  minutesFromNow,
  timeOf,
  tomorrowMorning,
  untilLabel,
  workingHoursSummary,
} from "@/util/availability";
import { apiErrorMessage } from "@/util/apiError";
import { cn } from "@/lib/utils";

interface StatusMenuProps {
  isCollapsed: boolean;
}

const UNAVAILABLE_FOR = [
  { label: "For 30 minutes", minutes: 30 },
  { label: "For 1 hour", minutes: 60 },
  { label: "For 2 hours", minutes: 120 },
];

export function StatusMenu({ isCollapsed }: StatusMenuProps) {
  const { data: presence } = useGetUserPresenceQuery();
  const [updatePresence] = useUpdateUserPresenceMutation();
  const [updateWorkingHours] = useUpdateWorkingHoursMutation();
  const [isHoursDialogOpen, setIsHoursDialogOpen] = useState(false);
  const [followHoursOnSave, setFollowHoursOnSave] = useState<boolean | undefined>(undefined);

  const description = describeAvailability(presence);
  const currentMode: AvailabilityMode = presence?.availabilityMode ?? "ALWAYS";
  const isManuallyUnavailable = isSetUnavailable(presence);
  const hasHours = hasWorkingHours(presence?.workingHours);
  const statusDotClass = description.isAvailable ? "bg-success" : "bg-destructive";

  const applyStatus = async (request: UserPresenceStatusRequest) => {
    try {
      await updatePresence(request).unwrap();
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Could not change your status"));
    }
  };

  const chooseWorkingHours = () => {
    if (!hasHours) {
      setFollowHoursOnSave(true);
      setIsHoursDialogOpen(true);
      return;
    }
    applyStatus({ status: "ONLINE", unavailableUntil: null, availabilityMode: "WORKING_HOURS" });
  };

  const editWorkingHours = () => {
    setFollowHoursOnSave(undefined);
    setIsHoursDialogOpen(true);
  };

  const trigger = isCollapsed ? (
    <SidebarMenuButton size="lg" tooltip={`${description.label}, ${description.detail}`}>
      <div className="flex aspect-square size-8 items-center justify-center">
        <span className={cn("size-3 rounded-full", statusDotClass)} />
      </div>
    </SidebarMenuButton>
  ) : (
    <button
      type="button"
      className="mb-3 flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors hover:bg-sidebar-accent"
    >
      <span className={cn("size-2.5 shrink-0 rounded-full", statusDotClass)} />
      <span className="grid min-w-0 flex-1 leading-tight">
        <span className="text-sm font-medium">{description.label}</span>
        <span className="truncate text-xs text-muted-foreground">{description.detail}</span>
      </span>
      <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent className="w-64 rounded-lg" align="start" side="right" sideOffset={4}>
          <DropdownMenuLabel className="text-xs text-muted-foreground">Your status</DropdownMenuLabel>
          <DropdownMenuItem
            className="gap-2.5 p-2"
            onClick={() => applyStatus({ status: "ONLINE", unavailableUntil: null, availabilityMode: "ALWAYS" })}
          >
            <span className="size-2.5 shrink-0 rounded-full bg-success" />
            <span className="grid flex-1 leading-tight">
              <span>Available</span>
              <span className="text-xs text-muted-foreground">Calls always ring you</span>
            </span>
            {!isManuallyUnavailable && currentMode === "ALWAYS" && <Check className="size-4 text-primary" />}
          </DropdownMenuItem>
          <DropdownMenuItem className="gap-2.5 p-2" onClick={chooseWorkingHours}>
            <Clock className="size-4 shrink-0 text-success" />
            <span className="grid min-w-0 flex-1 leading-tight">
              <span>Working hours</span>
              <span className="truncate text-xs text-muted-foreground">
                {hasHours ? workingHoursSummary(presence?.workingHours) : "Set your hours"}
              </span>
            </span>
            {!isManuallyUnavailable && currentMode === "WORKING_HOURS" && <Check className="size-4 text-primary" />}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <div className="flex items-center gap-2.5 px-2 py-1.5 text-sm">
            <span className="size-2.5 shrink-0 rounded-full bg-destructive" />
            <span className="grid flex-1 leading-tight">
              <span>Unavailable</span>
              <span className="text-xs text-muted-foreground">
                {isManuallyUnavailable ? description.detail : "Calls skip you"}
              </span>
            </span>
          </div>
          {UNAVAILABLE_FOR.map((option) => (
            <DropdownMenuItem
              key={option.minutes}
              className="pl-8"
              onClick={() =>
                applyStatus({
                  status: "OFFLINE",
                  unavailableUntil: minutesFromNow(option.minutes),
                  availabilityMode: currentMode,
                })
              }
            >
              <span className="flex-1">{option.label}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {untilLabel(minutesFromNow(option.minutes))}
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            className="pl-8"
            onClick={() =>
              applyStatus({ status: "OFFLINE", unavailableUntil: tomorrowMorning(), availabilityMode: currentMode })
            }
          >
            <span className="flex-1">Until tomorrow</span>
            <span className="text-xs tabular-nums text-muted-foreground">{timeOf(new Date(tomorrowMorning()))}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="pl-8"
            onClick={() => applyStatus({ status: "OFFLINE", unavailableUntil: null, availabilityMode: currentMode })}
          >
            Until I turn it back on
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="gap-2.5 p-2" onClick={editWorkingHours}>
            <CalendarDays className="size-4 text-muted-foreground" />
            Edit working hours
          </DropdownMenuItem>
          <p className="px-2 pb-1.5 pt-1 text-xs text-muted-foreground">A transfer someone picks you for still rings.</p>
        </DropdownMenuContent>
      </DropdownMenu>

      <WorkingHoursDialog
        open={isHoursDialogOpen}
        onOpenChange={setIsHoursDialogOpen}
        title="Your working hours"
        presence={presence}
        defaultTimeZone={browserTimeZone()}
        followHours={followHoursOnSave}
        onSave={(request) => updateWorkingHours(request).unwrap()}
      />
    </>
  );
}
